/**
 * Party mode — the table plays a lineup of games in turn, on its own.
 *
 * The host picks the games and how the table moves between them:
 *
 * - `rounds`: every game gets the same number of rounds, and the lineup loops
 *   until the host stops it.
 * - `schedule`: the night has a length, and every game gets an equal share of
 *   it. `laps` runs the lineup more than once inside the same window, in
 *   shorter slots.
 *
 * The switch itself only ever lands between hands, so a slot that runs out
 * mid-hand waits for the hand. The schedule absorbs the overrun: each new slot
 * is an equal share of the time that is actually left, so the night still
 * ends on time and no game is shorted by more than the hand it waited on.
 *
 * Everything here is pure — the server holds the running party, this decides
 * what the plan means, and the TV, the phone and the tests read the same maths.
 */
import { GAME_LABELS, isGameType, type GameType } from './types.js';

export type PartyMode = 'rounds' | 'schedule';

/** What the host asked for, cleaned and clamped. */
export interface PartyPlan {
  /** The lineup, in play order. At least two games, no repeats. */
  games: GameType[];
  mode: PartyMode;
  /** Rounds mode: rounds dealt on each game before the next one comes up. */
  roundsPerGame: number;
  /** Schedule mode: the whole party, start to finish. */
  durationMinutes: number;
  /** Schedule mode: times through the lineup inside the window. */
  laps: number;
}

export const MIN_PARTY_GAMES = 2;

/** No slot shorter than this: a game needs time to be worth switching to. */
export const MIN_PARTY_SLOT_MINUTES = 5;

export const PARTY_LIMITS = {
  roundsPerGame: { min: 1, max: 50, default: 5 },
  durationMinutes: { min: 10, max: 12 * 60, default: 120 },
  laps: { min: 1, max: 12, default: 1 },
} as const;

const clampInt = (value: unknown, limits: { min: number; max: number; default: number }): number => {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : limits.default;
  return Math.min(limits.max, Math.max(limits.min, n));
};

/**
 * Keep only real games, first mention wins. Unlike a poll ballot the order is
 * the host's: it is the order the table will play them in.
 */
export function normalizePartyGames(requested: unknown): GameType[] {
  if (!Array.isArray(requested)) return [];
  const games: GameType[] = [];
  for (const game of requested) {
    if (isGameType(game) && !games.includes(game)) games.push(game);
  }
  return games;
}

/** Clean a plan off the wire. Numbers are clamped, never refused. */
export function normalizePartyPlan(input: unknown): PartyPlan {
  const raw = (typeof input === 'object' && input !== null ? input : {}) as Record<string, unknown>;
  return {
    games: normalizePartyGames(raw.games),
    mode: raw.mode === 'schedule' ? 'schedule' : 'rounds',
    roundsPerGame: clampInt(raw.roundsPerGame, PARTY_LIMITS.roundsPerGame),
    durationMinutes: clampInt(raw.durationMinutes, PARTY_LIMITS.durationMinutes),
    laps: clampInt(raw.laps, PARTY_LIMITS.laps),
  };
}

/** Slots in the whole party; a rounds party loops forever. */
export function partySlotCount(plan: PartyPlan): number {
  return plan.mode === 'schedule' ? plan.games.length * plan.laps : Number.POSITIVE_INFINITY;
}

/** Schedule mode: each game's share of the window, in minutes. */
export function partySlotMinutes(plan: PartyPlan): number {
  return plan.durationMinutes / (plan.games.length * plan.laps);
}

/** Why a cleaned plan cannot run, or null when it can. */
export function partyPlanProblem(plan: PartyPlan): string | null {
  if (plan.games.length < MIN_PARTY_GAMES) return `Pick at least ${MIN_PARTY_GAMES} games.`;
  if (plan.mode === 'schedule' && partySlotMinutes(plan) < MIN_PARTY_SLOT_MINUTES) {
    return `That gives each game under ${MIN_PARTY_SLOT_MINUTES} minutes — add time or drop a lap.`;
  }
  return null;
}

/** The game that plays in a slot. Slots count up from 0 across laps. */
export const partyGameAt = (plan: PartyPlan, slot: number): GameType =>
  plan.games[slot % plan.games.length]!;

/**
 * When the next slot ends: an equal share of the time still left, split over
 * the slots still to play (this one included). Late switches shrink every
 * slot after them a little, instead of pushing the end of the night back.
 */
export function nextSlotEndsAt(now: number, endsAt: number, slotsLeft: number): number {
  if (slotsLeft <= 1) return endsAt;
  return now + Math.max(0, endsAt - now) / slotsLeft;
}

/** A running party, as it rides in `RoomState`. */
export interface PublicParty {
  mode: PartyMode;
  games: GameType[];
  /** The game playing now, and the one lined up after it. */
  current: GameType;
  /** Null once the last scheduled slot is playing. */
  next: GameType | null;
  /** 0-based slot, counting across laps. */
  slot: number;
  /** 1-based lap through the lineup. */
  lap: number;
  /** Schedule mode: laps planned. Null for a looping rounds party. */
  laps: number | null;
  /** Rounds mode: the target per game, and how many this game has dealt. */
  roundsPerGame: number | null;
  roundsPlayed: number | null;
  /** Schedule mode: when this game's slot, and the whole party, run out. */
  slotEndsAt: number | null;
  endsAt: number | null;
  /** The slot is up; the switch lands as soon as the hand in play ends. */
  due: boolean;
  /** The schedule has run out. The felt stays where it is. */
  done: boolean;
}

/** "Blackjack → Roulette → Craps", for a lineup line. */
export const partyLineup = (games: readonly GameType[]): string =>
  games.map((game) => GAME_LABELS[game]).join(' → ');
